import {useState, useEffect} from 'react';
import {useParams} from 'react-router';
import {Button, Card, Divider, Row, Col, Input, message} from 'antd';
import {UserSelectWidget} from "./UserSelectWidget";
import {
    getTask, updateTaskAssignedTo, updateTaskDescription, updateTaskTitle,
    getTaskNotes, updateTaskDueDate, insertTaskFollower, getTaskFollowers,
    deleteTaskFollower
} from "../../api/Api";
import {DatePickerWidget} from "../widget/DatePickerWidget";
import {TaskStatusWidget} from "./TaskStatusWidget";
import {TaskNotesTable} from "./TaskNotesTable";
import {NewTaskNotesWidget} from "./NewTaskNotesWidget";
import {TASK_DESCRIPTION_MAX_LENGTH} from "../../api/Constants";
import {FollowerSelectWidget} from "./FollowerSelectWidget";

const {TextArea} = Input;

export const TaskView = ({session, refresh, onRefresh, onApiFail, props}) => {
    const [task, setTask] = useState(undefined);
    const params = useParams();
    const taskId = params.taskId;
    const [messageApi, contextHolder] = message.useMessage();

    const [title, setTitle] = useState(undefined);
    const [description, setDescription] = useState(undefined);
    const [notes, setNotes] = useState([]);
    const [followers, setFollowers] = useState([]);

    let userDisable = false;
    if ((task !== undefined) && (session.id !== task.createdBy)) {
        // task created by other user
        userDisable = true;
    }
    // else {
    //     console.log(`task ${JSON.stringify(task)} session ${JSON.stringify(session)}`);
    // }

    useEffect(() => {
        if (taskId === undefined) {
            return;
        }
        getTask(taskId).then(result => {
            setTask(result);
            setTitle(result.title);
            setDescription(result.description);

            return getTaskNotes(result.id);
        }).then(r => {
            setNotes(r.map(note => {
                return {
                    ...note,
                    key: note.id
                }
            }).sort((a, b) => (new Date(a.timestamp)) - (new Date(b.timestamp))))
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });

        getTaskFollowers(taskId).then(r => {
            setFollowers(r.map(follower => {
                return {
                    ...follower,
                    key: follower.id
                }
            }));
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }, [taskId, refresh, onApiFail]);

    const onTitleChange = (e) => {
        // if it's disabled don't allow the change
        if (userDisable) return;
        setTitle(e.target.value);
    }

    const onDescriptionChange = (e) => {
        // if it's disabled don't allow the change
        if (userDisable) return;
        setDescription(e.target.value);
    }

    const onDueDateChange = (dateString) => {
        updateTaskDueDate(task.id, dateString).then(r => {
            messageApi.open({
                type: "success",
                content: "Due Date Updated"
            });
            if (onRefresh) onRefresh();
        }).catch (e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
            messageApi.open({
                type: "error",
                content: "Failed to Update Due Date"
            })
        })
    }

    const setAssignment = (val) => {
        // console.log(`assigned to ${JSON.stringify(val)}`);
        updateTaskAssignedTo(task.id, val.id).then(r => {
            messageApi.open({
                type: "success",
                content: "Task Updated"
            });
            if (onRefresh) onRefresh();
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
            messageApi.open({
                type: "error",
                content: "Failed to Update Task"
            })
        })
    }

    const saveTask = () => {
        const ioPromises = [];
        if (title !== task.title) {
            ioPromises.push(updateTaskTitle(task.id, title));
        }
        if (description !== task.description) {
            ioPromises.push(updateTaskDescription(task.id, description));
        }
        Promise.all(ioPromises).then(r => {
            messageApi.open({
                type: "success",
                content: "Task Updated"
            });
            if (onRefresh) onRefresh();
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
            messageApi.open({
                type: "error",
                content: "Failed to Update Task"
            })
        })
    }

    const addFollower = (follower) => {
        let followerExists = false;
        followers.forEach(f => {
            if (f.id === follower.id) {
                followerExists = true;
            }
        });
        if (!followerExists) {
            insertTaskFollower(taskId, [follower]).then(r => {
                if (onRefresh) onRefresh();
            }).catch(e => {
                if ((e.status === 401) && (onApiFail)) {
                    onApiFail();
                }
            });
        }
    }

    const removeFollower = (follower) => {
        deleteTaskFollower(taskId, follower.id).then(r => {
            if (onRefresh) onRefresh();
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }

    const rowStyle = {
        marginTop: '10px',
        marginBottom: '20px'
    }

    // const dueDateInvalid = (dueDate === null) || (dueDate === undefined);
    // const titleInvalid = (title === null) || (title === undefined) || title === "";
    // const assignToInvalid = assignTo === null || assignTo === undefined;
    // const saveDisabled = dueDateInvalid || titleInvalid;

    if (task === undefined) {
        return <div />
    }

    const saveDisabled = (title === task.title) && (description === task.description);
    let saveMsg = "";
    if (title !== task.title) {
        saveMsg += "Title Unsaved.  "
    }
    if (description !== task.description) {
        saveMsg += "Description Unsaved."
    }

    const {createdBy} = task;
    const allowVerified = session.id === createdBy;

    return <div style={{textAlign: 'left'}}>
        {contextHolder}
        <Card title={"Task Information"} >
            <Row style={rowStyle}>
                <TextArea value={title} placeholder={'title'} showCount rows={1} maxLength={256} onChange={onTitleChange} />
            </Row>
            <Row style={rowStyle}>
                <TextArea value={description} placeholder={`description`} showCount rows={8} maxLength={TASK_DESCRIPTION_MAX_LENGTH} onChange={onDescriptionChange} />
            </Row>
            {userDisable !== true &&
            <Row style={rowStyle}>
                <Col span={4}>
                    <Button onClick={saveTask} disabled={saveDisabled}>Save Changes</Button>
                </Col>
                <Col style={{color: "#f55442"}}>
                    {saveMsg}
                </Col>
            </Row>
            }
            <Row style={rowStyle}>
                <Col span={2}>
                    <b>Created By:</b>
                </Col>
                <Col>
                    {`${task.creator.firstname} ${task.creator.lastname}`}
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={2} style={{marginTop: '5px'}}>
                    <b>Due Date:</b>
                </Col>
                <Col>
                    <DatePickerWidget date={task.dueDate} placeholder={"due date"} onChange={onDueDateChange} disabled={userDisable}/>
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={2}>
                    <b>Assigned To:</b>
                </Col>
                <Col>
                    <UserSelectWidget user={task.assignee} altText={`assign to`} onApiFail={onApiFail} setUser={setAssignment} />
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={2}>
                    <b>Status: </b>
                </Col>
                <Col>
                    <TaskStatusWidget task={task} onApiFail={onApiFail} onRefresh={onRefresh} allowVerified={allowVerified} />
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={2}>
                    <b>Followers: </b>
                </Col>
                <Col>
                    <FollowerSelectWidget onRefresh={onRefresh} refresh={refresh} onApiFail={onApiFail}
                          followers={followers} addFollower={addFollower} removeFollower={removeFollower} />
                </Col>
            </Row>
        </Card>
        <Card>
            <TaskNotesTable notes={notes} />
            <Divider />
            <NewTaskNotesWidget task={task} onApiFail={onApiFail} onRefresh={onRefresh}/>
        </Card>
    </div>
}